import { useConfig } from "../containers/ConfigContext";
import {
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  deleteComplain,
  getComplainsByEventId,
  updateComplain,
} from "../actions/complain.action";
import TableList from "../components/TableList";
import { HeadCell } from "../models/base.model";
import {
  Complain,
  ComplainLog,
  ComplainStatus,
} from "../models/complain.model";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { getAllowedCompanyStores } from "../actions/company.store.action";
import { getQuestions } from "../actions/question.action";
import { Question } from "../models/question.model";
import { getSubmitAnswers } from "../actions/survey.event.action";
import { complainReasonMap, filterOnKeys, titleStr } from "../lib/helper";
import { FilterGroup, FilterType } from "../components/TableFilter";
import { blue, green, grey, purple, red } from "@mui/material/colors";
import SurveySubmitAnswerView from "../containers/SurveySubmitAnswerView";
import { CloseComplainForm } from "./CloseComplainForm";
import _ from "lodash";
import InsertChartIcon from "@mui/icons-material/InsertChart";
import EditIcon from "@mui/icons-material/Edit";
import BookIcon from "@mui/icons-material/Book";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import FormatIndentIncreaseIcon from "@mui/icons-material/FormatIndentIncrease";
import {
  SurveyComplainAddNoteForm,
  SurveyComplainSelectReasonForm,
} from "./SurveyComplainForms";
import SurveyComplainLogs from "./SurveyComplainLogs";
import SurveyComplainDetails from "./SurveyComplainDetails";

let cached_stores: Record<string, string> | undefined;

export const COMPLAIN_STATUS_COLOR_MAP: Record<ComplainStatus, string> = {
  [ComplainStatus.open]: green[500],
  [ComplainStatus.closed]: grey[500],
  [ComplainStatus.viewed]: blue[500],
  [ComplainStatus.inProgress]: purple[500],
};

const actionMap: Record<
  ComplainStatus,
  {
    nextActionText: string;
    nextStatus: ComplainStatus;
    nextHelperText: string;
  }
> = {
  [ComplainStatus.open]: {
    nextActionText: "View",
    nextStatus: ComplainStatus.viewed,
    nextHelperText: "",
  },
  [ComplainStatus.viewed]: {
    nextActionText: "In Progress",
    nextStatus: ComplainStatus.inProgress,
    nextHelperText:
      "This will mark the complain as being handled. Please contact the customer promptly.",
  },
  [ComplainStatus.inProgress]: {
    nextActionText: "Close",
    nextStatus: ComplainStatus.closed,
    nextHelperText: "Are you sure you want to close this complain?",
  },
  [ComplainStatus.closed]: {
    nextActionText: "Reopen",
    nextStatus: ComplainStatus.inProgress,
    nextHelperText:
      "This complain is closed. Reopen it to continue processing?",
  },
};

export function SurveyComplains(props: { eventId: string }) {
  const { eventId } = props;
  const tableRef = useRef<any>(null);
  const closeFormRef = useRef<any>(null);
  const addNoteFormRef = useRef<any>(null);
  const selectReasonFormRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const { loggedUser } = useConfig();

  const defaultHeadCells: HeadCell[] = [
    {
      id: "submitId",
      label: "ID",
    },
    {
      id: "storeId",
      label: "Site",
      render: (value: string) => {
        return <div>{cached_stores?.[value]}</div>;
      },
    },
    {
      id: "visitDate",
      label: "Visit Date",
      render: (value: string) => value?.replace(/_/g, "-"),
    },
    {
      id: "reason",
      label: "Reason for dissatisfaction",
      render: (value: string) => {
        return (
          <div>
            {complainReasonMap[value as keyof typeof complainReasonMap]}
          </div>
        );
      },
    },
    {
      id: "status",
      label: "Status",
      render: (value: ComplainStatus) => (
        <Chip
          label={titleStr(value)}
          sx={{
            backgroundColor: COMPLAIN_STATUS_COLOR_MAP[value],
            color: "#fff",
          }}
        />
      ),
    },
    {
      id: "escalate",
      label: "Escalate",
      render: (value: string) => {
        return (
          <Chip
            label={titleStr(value)}
            sx={{
              backgroundColor: value === "yes" ? red[500] : grey[500],
              color: "#fff",
            }}
          />
        );
      },
    },
    {
      id: "createdAt",
      label: "Created At",
      render: (value) => (
        <div>{moment(value).format("YYYY-MM-DD HH:mm:ss")}</div>
      ),
    },
  ];

  const [headCells, setHeadCells] = useState<HeadCell[]>(defaultHeadCells);

  useEffect(() => {
    load();
    return () => {
      cached_stores = undefined;
    };
  }, []);

  const load = async () => {
    const stores = await getAllowedCompanyStores();
    cached_stores = stores.reduce((acc, store) => {
      acc[store.id] = store.name;
      return acc;
    }, {} as Record<string, string>);
    setLoading(false);
  };

  const onEscalate = async (complain: Complain) => {
    const escalate = complain.escalate === "yes" ? "no" : "yes";
    await updateComplain({
      eventId: complain.eventId,
      submitId: complain.submitId,
      escalate,
    });
    await tableRef.current?.onEdit({ ...complain, escalate });
    await window.webApp.toggleDialog({
      title: "",
      open: false,
      element: <></>,
      onOk: async () => {},
    });
  };

  const onSearch = async (value: string, all: Complain[]) => {
    return all
      ? {
          data: all.filter((a) =>
            filterOnKeys(a, ["id", "status", "contact"], value)
          ),
        }
      : { data: [] };
  };

  const filters: FilterGroup<Complain>[] = [
    {
      title: "Filter by Status",
      items: [
        {
          name: "status",
          type: FilterType.selection,
          options: Object.values(ComplainStatus).map((status) => ({
            label: titleStr(status),
            value: status,
          })),
        },
      ],
    },
    {
      title: "Filter by Site",
      items: [
        {
          name: "storeId",
          type: FilterType.selection,
          options: Object.entries(cached_stores || {}).map(([key, value]) => ({
            label: value,
            value: key,
          })),
          label: "Site",
          multiple: true,
        },
      ],
    },
    {
      title: "Filter by Reason",
      items: [
        {
          name: "reason",
          type: FilterType.selection,
          options: Object.entries(complainReasonMap).map(([key, value]) => ({
            label: value,
            value: key,
          })),
        },
      ],
    },
    {
      title: "Filter by Escalate",
      items: [
        {
          name: "escalate",
          type: FilterType.selection,
          options: [
            {
              label: "Yes",
              value: "yes",
            },
            {
              label: "No",
              value: "no",
            },
          ],
        },
      ],
    },
  ];

  const renderActions = useCallback((complain: Complain) => {
    return [
      {
        label: actionMap[complain.status].nextActionText,
        icon: <EditIcon />,
        onClick: async () => {
          let { answers: allAnswers, questions } = await getSubmitAnswers({
            eventId: complain.eventId,
            submitId: complain.submitId,
          });
          const questionAnswers = Object.fromEntries(
            Object.entries(allAnswers)
          );
          if (complain.status === ComplainStatus.open) {
            complain.status = ComplainStatus.viewed;
            await updateComplain({
              eventId: complain.eventId,
              submitId: complain.submitId,
              status: ComplainStatus.viewed,
            });
            tableRef.current?.onEdit({ ...complain });
          }
          window.webApp.toggleDialog({
            title: `Details`,
            element: (
              <SurveyComplainDetails
                complain={complain}
                onEscalate={onEscalate}
                questionAnswers={questionAnswers}
                questions={questions}
              />
            ),
            minWidth: 800,
            onOk: async () => {
              const nextStatus = actionMap[complain.status].nextStatus;
              await window.webApp.toggleConfirmDialog({
                title: `Mark as ${titleStr(nextStatus)}`,
                element: (
                  <CloseComplainForm
                    helperText={actionMap[complain.status].nextHelperText}
                    ref={closeFormRef}
                    nextStatus={nextStatus}
                  />
                ),
                onOk: async () => {
                  const values = await closeFormRef.current?.onSubmit();
                  await updateComplain({
                    eventId: complain.eventId,
                    submitId: complain.submitId,
                    status: nextStatus,
                    ...values,
                  });
                  await tableRef.current?.onEdit({
                    ...complain,
                    status: nextStatus,
                  });
                },
                open: true,
              });
            },
            okText: `Mark as ${titleStr(
              actionMap[complain.status].nextStatus
            )}`,
            open: true,
            noRightTopX: true,
          });
        },
      },
      {
        label: "View Answers",
        icon: <QuestionAnswerIcon />,
        onClick: async () => {
          let { answers: allAnswers, questions } = await getSubmitAnswers({
            eventId: complain.eventId,
            submitId: complain.submitId,
          });
          const questionAnswers = Object.fromEntries(
            Object.entries(allAnswers)
          );
          window.webApp.toggleDialog({
            title: "Answers",
            element: <SurveySubmitAnswerView answers={questionAnswers} questions={questions} />,
            minWidth: 800,
            onOk: async () => {},
            open: true,
          });
        },
      },
      {
        label: "View Logs",
        icon: <BookIcon />,
        onClick: async () => {
          window.webApp.toggleDialog({
            title: "Logs",
            element: (
              <SurveyComplainLogs
                eventId={complain.eventId}
                submitId={complain.submitId}
              />
            ),
            minWidth: 800,
            onOk: async () => {},
            noCancel: true,
            okText: "Close",
            open: true,
          });
        },
      },
      {
        label: "Leave a note",
        icon: <InsertChartIcon />,
        onClick: async () => {
          window.webApp.toggleConfirmDialog({
            title: `Leave a note`,
            element: <SurveyComplainAddNoteForm ref={addNoteFormRef} />,
            onOk: async () => {
              const values = await addNoteFormRef.current?.onSubmit();
              await updateComplain({
                eventId: complain.eventId,
                submitId: complain.submitId,
                ...values,
              });
            },
            open: true,
          });
        },
      },
      {
        label: "Reason for dissatisifcation",
        icon: <FormatIndentIncreaseIcon />,
        onClick: async () => {
          window.webApp.toggleConfirmDialog({
            title: `Reason for dissatisifcation`,
            element: (
              <SurveyComplainSelectReasonForm
                ref={selectReasonFormRef}
                defaultValues={complain}
              />
            ),
            onOk: async () => {
              const values = await selectReasonFormRef.current?.onSubmit();
              await updateComplain({
                eventId: complain.eventId,
                submitId: complain.submitId,
                ...values,
              });
              await tableRef.current?.onEdit({ ...complain, ...values });
            },
            open: true,
          });
        },
      },
    ];
  }, []);

  return (
    <div>
      <div
        style={{
          display: loading ? "flex" : "none",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "10rem",
        }}
      >
        <CircularProgress />
      </div>
      {!loading && (
        <TableList<Complain>
          key={eventId}
          headCells={headCells}
          uid="submitId"
          onLoad={async () => {
            const res = await getComplainsByEventId(eventId);
            return {
              data: res,
              total: res.length,
            };
          }}
          ref={tableRef}
          onSearch={onSearch}
          filters={filters}
          actions={renderActions}
        />
      )}
    </div>
  );
}
